import type { ReactElement } from 'react'

/** Theme-tinted stack marks. Paths stay monochrome via currentColor. */

type MarkProps = { className?: string }

const marks: Record<string, (p: MarkProps) => ReactElement> = {
  React: ({ className }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="2.2" fill="currentColor" stroke="none" />
      <ellipse cx="12" cy="12" rx="10" ry="4" />
      <ellipse cx="12" cy="12" rx="10" ry="4" transform="rotate(60 12 12)" />
      <ellipse cx="12" cy="12" rx="10" ry="4" transform="rotate(120 12 12)" />
    </svg>
  ),
  TypeScript: ({ className }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="currentColor">
      <path d="M2 4h20v16H2V4zm9.2 5.2v2H8.5v7.1H6.2V11.2H3.5V9.2h7.7zm9.3 4.1c0 2.2-1.3 3.5-3.5 3.5-1.1 0-2-.4-2.6-1l1.1-1.4c.4.4.9.7 1.5.7.8 0 1.3-.4 1.3-1.2 0-.8-.5-1.2-1.6-1.2h-.8v-1.7h.8c.9 0 1.4-.4 1.4-1.1 0-.6-.4-1-1.1-1-.6 0-1.1.3-1.4.7l-1.2-1.3c.7-.8 1.7-1.3 2.9-1.3 1.9 0 3.1 1.1 3.1 2.6 0 1-.5 1.7-1.4 2.1.9.3 1.5 1.1 1.5 2.3z" />
    </svg>
  ),
  JavaScript: ({ className }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="currentColor">
      <path d="M2 2h20v20H2V2zm10.4 14.4c0 2.1-1.2 3.1-3.1 3.1-1.6 0-2.6-.8-3.1-1.8l1.7-1c.3.5.6.9 1.3.9.7 0 1.1-.3 1.1-1.5v-6.6h2.1v6.9zm5.7 3.1c-1.9 0-3.1-1-3.7-2.2l1.7-1c.4.7.9 1.2 1.9 1.2.8 0 1.3-.4 1.3-1 .0-.7-.5-1-1.4-1.4l-.5-.2c-1.4-.6-2.3-1.4-2.3-3 0-1.5 1.1-2.6 2.9-2.6 1.3 0 2.2.4 2.9 1.6l-1.6 1c-.3-.5-.7-.8-1.3-.8-.6 0-1 .4-1 1 0 .6.4 1 1.3 1.4l.5.2c1.6.7 2.5 1.5 2.5 3.1 0 1.8-1.4 2.7-3.1 2.7z" />
    </svg>
  ),
  Nextjs: ({ className }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="currentColor">
      <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm4.2 14.6h-1.5l-4.8-7.1v7.1H8.5V7.4h1.6l4.7 7v-7h1.4v9.2z" />
    </svg>
  ),
  Electron: ({ className }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.4">
      <ellipse cx="12" cy="12" rx="10" ry="4.5" />
      <ellipse cx="12" cy="12" rx="10" ry="4.5" transform="rotate(60 12 12)" />
      <ellipse cx="12" cy="12" rx="10" ry="4.5" transform="rotate(120 12 12)" />
      <circle cx="12" cy="12" r="1.8" fill="currentColor" stroke="none" />
    </svg>
  ),
  Tauri: ({ className }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6">
      <circle cx="9" cy="12" r="3.2" />
      <circle cx="15" cy="12" r="3.2" />
      <path d="M4 12h2.2M17.8 12H20" />
    </svg>
  ),
  Supabase: ({ className }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="currentColor">
      <path d="M13.4 3.2a1 1 0 00-1.8.2L7.2 16.4A1.2 1.2 0 008.3 18h5.5l-1.1 3.5a1 1 0 001.8.7l7.2-10.2A1.2 1.2 0 0020.6 10h-5.8l1.4-5.6a1 1 0 00-.8-1.2z" />
    </svg>
  ),
  Firebase: ({ className }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="currentColor">
      <path d="M5.5 17.8L8.2 3.9a.5.5 0 01.9-.2l2.2 4.1-5.8 10zm13.2.1l-2.4-14a.5.5 0 00-.9-.2L5.5 17.9l6.2 3.5a2 2 0 001.9 0l5.1-3.5z" />
    </svg>
  ),
  Tailwind: ({ className }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="currentColor">
      <path d="M12 6c-2.7 0-4.4 1.3-5 4 1-1.3 2.1-1.8 3.4-1.5.7.2 1.3.7 1.8 1.3C13.2 11 14.2 12 16 12c2.7 0 4.4-1.3 5-4-1 1.3-2.1 1.8-3.4 1.5-.7-.2-1.3-.7-1.8-1.3C14.8 7 13.8 6 12 6zM7 12c-2.7 0-4.4 1.3-5 4 1-1.3 2.1-1.8 3.4-1.5.7.2 1.3.7 1.8 1.3C8.2 17 9.2 18 11 18c2.7 0 4.4-1.3 5-4-1 1.3-2.1 1.8-3.4 1.5-.7-.2-1.3-.7-1.8-1.3C9.8 13 8.8 12 7 12z" />
    </svg>
  ),
  Leaflet: ({ className }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="currentColor">
      <path d="M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5A2.5 2.5 0 1112 6a2.5 2.5 0 010 5.5z" />
    </svg>
  ),
  OpenAI: ({ className }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="currentColor">
      <path d="M22 11.4a4.3 4.3 0 00-2.3-3.7 4.3 4.3 0 00-.3-4.2A4.3 4.3 0 0015 2a4.3 4.3 0 00-3.7-2 4.3 4.3 0 00-4 2.7A4.3 4.3 0 002.7 5a4.3 4.3 0 00.3 4.2A4.3 4.3 0 002 12.6a4.3 4.3 0 002.3 3.7 4.3 4.3 0 00.3 4.2A4.3 4.3 0 009 22a4.3 4.3 0 003.7 2 4.3 4.3 0 004-2.7 4.3 4.3 0 004.6-2.3 4.3 4.3 0 00-.3-4.2A4.3 4.3 0 0022 11.4z" />
    </svg>
  ),
  Python: ({ className }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="currentColor">
      <path d="M12 2C7.5 2 7.8 4 7.8 4v2.2h4.3v.7H5.5S3 6.7 3 11.3s2 4.5 2 4.5h1.5v-2.2s-.1-2.2 2.2-2.2h3.8s2.1 0 2.1-2.1V4.2S14.8 2 12 2zm-2 1.4a.8.8 0 110 1.6.8.8 0 010-1.6zM12 22c4.5 0 4.2-2 4.2-2v-2.2h-4.3v-.7h6.6S21 17.3 21 12.7s-2-4.5-2-4.5h-1.5v2.2s.1 2.2-2.2 2.2H11.5S9.4 12.6 9.4 14.7v5.1S9.2 22 12 22zm2-1.4a.8.8 0 110-1.6.8.8 0 010 1.6z" />
    </svg>
  ),
  Deno: ({ className }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="currentColor">
      <circle cx="12" cy="12" r="10" />
      <circle cx="15" cy="9" r="1.2" fill="#000" />
    </svg>
  ),
  Expo: ({ className }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="currentColor">
      <path d="M12 3.2L3.5 19.5h4.2L12 8.8l4.3 10.7h4.2L12 3.2z" />
    </svg>
  ),
  Vite: ({ className }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="currentColor">
      <path d="M12 2L2.5 5.5 12 22l9.5-16.5L12 2zm0 4.2l4.8 1.7L12 16.8 7.2 7.9 12 6.2z" />
    </svg>
  ),
  ChromaDB: ({ className }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6">
      <circle cx="8" cy="12" r="3" />
      <circle cx="16" cy="8" r="2.5" />
      <circle cx="16" cy="16" r="2.5" />
      <path d="M10.8 10.5l2.7-1.5M10.8 13.5l2.7 1.5" />
    </svg>
  ),
  Recharts: ({ className }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M4 19V5M4 19h16" />
      <path d="M8 15l3-4 3 2 4-6" />
    </svg>
  ),
  Telethon: ({ className }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="currentColor">
      <path d="M3 11.5L21 3l-3.5 17.5-6-4.2-3.2 3.1.5-5.3L17 7.2 8.4 13.6 3 11.5z" />
    </svg>
  ),
  macOS: ({ className }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="currentColor">
      <path d="M16.5 3c.2 1.4-.4 2.7-1.2 3.6-.9.9-2.2 1.6-3.5 1.5-.2-1.3.5-2.7 1.2-3.5C14 3.6 15.3 3 16.5 3zM20 17.2c-.5 1.1-.8 1.6-1.4 2.6-1 1.3-2.1 2.9-3.7 2.9-1.4 0-1.8-.9-3.4-.9s-2.1.9-3.4.9c-1.5 0-2.7-1.5-3.7-2.8C2.7 17.4 1.7 13.7 3.5 11c.9-1.4 2.4-2.3 4-2.3 1.5 0 2.6.9 3.4.9.8 0 2.2-1.1 3.8-1 1 .1 2.3.5 3.1 1.6-2.7 1.5-2.3 5.4.2 6.9z" />
    </svg>
  ),
}

function resolveMark(name: string) {
  if (marks[name]) return marks[name]
  if (name === 'Next.js') return marks.Nextjs
  return null
}

export default function TechStack({ stack }: { stack: string[] }) {
  return (
    <ul className="flex flex-wrap gap-2">
      {stack.map((name) => {
        const Mark = resolveMark(name)
        return (
          <li
            key={name}
            className="inline-flex items-center gap-2 text-[11px] font-mono text-jax-link border border-jax-navy rounded px-2.5 py-1.5 bg-jax-surface"
          >
            {Mark ? <Mark className="w-3.5 h-3.5 text-jax-link shrink-0" /> : (
              <span className="w-1.5 h-1.5 rounded-full bg-jax-accent shrink-0" aria-hidden="true" />
            )}
            {name}
          </li>
        )
      })}
    </ul>
  )
}
